import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { v2AdminGetStartupOutreachContacts } from '@workspace/api-backend'
import {
  v2AdminGetStartupByIdQueryKey,
  v2AdminGetStartupOutreachOverviewOptions,
  v2AdminGetStartupPortfolioOutreachQueueOptions,
  v2AdminGetStartupPortfolioOutreachStatusOptions,
  v2AdminListOutreachBillingOptions,
} from '@workspace/api-backend/react-query'
import type { AiReportData } from '@workspace/seedforge-features/analytics'
import { AiReportSection } from '@workspace/seedforge-features/analytics'
import { ENTITLEMENT_KIND, ENTITLEMENT_STATUS } from '@workspace/seedforge-shared'
import { Prose } from '@workspace/seedforge-ui/components/prose'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { Switch } from '@workspace/ui/components/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@workspace/ui/components/tooltip'
import { cn, visitorLabel } from '@workspace/ui/lib/utils'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { StartupDealFlowInteractionsTab } from '../../../features/admin/components/deal-flow-interactions-tab'
import { ExperimentArmBadges } from '../../../features/admin/components/experiment-arms'
import { ForfeitBalanceConfirm } from '../../../features/admin/components/forfeit-balance-confirm'
import { HeyReachBlockers } from '../../../features/admin/components/heyreach-blockers'
import {
  type BillingRow,
  OutreachBillingRow,
  OutreachBillingTableHeader,
} from '../../../features/admin/components/outreach-billing-row'
import {
  type OutreachInsightContact,
  OutreachInsights,
  type OutreachInsightsPayload,
  PortfolioOutreachMessages,
  StartupOutreachMessages,
} from '../../../features/admin/components/outreach-insights'
import { StartupEmailUpdatesTab } from '../../../features/admin/components/startup-email-updates-tab'
import { StartupFactsTab } from '../../../features/admin/components/startup-facts-tab'
import {
  type AdminMembershipItem,
  useAdminAddStartupCreditsMutation,
  useAdminAiInsightReportsQuery,
  useAdminDeleteStartupMutation,
  useAdminDocumentDownloadUrlMutation,
  useAdminGrantStartupEntitlementMutation,
  useAdminGrantStartupUsageMutation,
  useAdminInvestorActivityResearchQuery,
  useAdminRefreshStartupMatchesMutation,
  useAdminRegenerateAiInsightReportMutation,
  useAdminResetStartupGrantsMutation,
  useAdminRevokeStartupEntitlementMutation,
  useAdminSetStartupInternalMutation,
  useAdminSetStartupProfileChatLimitMutation,
  useAdminStartupBillingQuery,
  useAdminStartupByIdQuery,
  useAdminStartupCreditsQuery,
  useAdminStartupDeletionPreviewQuery,
  useAdminStartupDocumentsQuery,
  useAdminStartupEntitlementsQuery,
  useAdminStartupMatchesQuery,
  useAdminStartupMatchesRefreshStatusQuery,
  useAdminStartupMembershipsQuery,
  useAdminStartupOutreachContactsQuery,
  useAdminStartupReactionsQuery,
  useAdminStartupTeamMembersQuery,
  useAdminStartupTextFeedbacksQuery,
  useAdminTriggerActivityResearchMutation,
} from '../../../features/admin/hooks/use-admin-api'
import {
  adminLimitsPendingQueryKey,
  adminPendingQueryKey,
  outreachApi,
} from '../../../features/outreach/api'
import {
  OutreachFunnel,
  PortfolioOutreachFunnel,
} from '../../../features/outreach/components/outreach-funnel'
import type { OutreachStatus } from '../../../features/outreach/types'
import {
  DocumentPreviewModal,
  type PreviewDocument,
} from '../../../features/preview/components/document-preview-modal'
import { apiErrorMessage } from '../../../lib/api-error'
import { formatUsdCents } from '../../../lib/format'
import { heyreach } from '../../../lib/heyreach'

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'documents', label: 'Documents' },
  { key: 'insights', label: 'Insights' },
  { key: 'billing', label: 'Billing' },
  { key: 'entitlements', label: 'Entitlements' },
  { key: 'matches', label: 'Investors' },
  { key: 'outreach', label: 'Outreach' },
  { key: 'deal-flow', label: 'Deal flow' },
  { key: 'facts', label: 'Facts' },
  { key: 'email-updates', label: 'Email updates' },
] as const

type Tab = (typeof tabs)[number]['key']

const TAB_KEYS = tabs.map((t) => t.key)

export const Route = createFileRoute('/admin/startups/$id')({
  component: AdminStartupDetailPage,
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => {
    const tab = search.tab
    return typeof tab === 'string' && (TAB_KEYS as readonly string[]).includes(tab)
      ? { tab: tab as Tab }
      : {}
  },
})

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm font-medium">{children}</div>
    </div>
  )
}

function AdminStartupDetailPage() {
  const { id } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const activeTab: Tab = tab ?? 'overview'
  const setActiveTab = (next: Tab) => {
    navigate({ search: { tab: next } })
  }

  const startupQuery = useAdminStartupByIdQuery(id)

  const startup = startupQuery.data?.data

  if (startupQuery.isLoading) {
    return <div className="py-12 text-center text-muted-foreground">Loading...</div>
  }

  if (!startup) {
    return <div className="py-12 text-center text-muted-foreground">Startup not found.</div>
  }

  return (
    <>
      <nav className="border-b border-border">
        <div className="-mb-px flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Logo */}
            {startup.logoUrl && (
              <div className="flex items-center gap-4">
                <img
                  src={startup.logoUrl}
                  alt={startup.name}
                  className="size-16 rounded-lg border border-border object-cover"
                />
              </div>
            )}

            {/* Core Fields */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoCard label="Name">{startup.name}</InfoCard>
              <InfoCard label="Product Stage">
                {startup.productStage ? (
                  <Badge variant="secondary" size="sm">
                    {startup.productStage}
                  </Badge>
                ) : (
                  '-'
                )}
              </InfoCard>
              <InfoCard label="Category">{startup.category?.name || '\u2014'}</InfoCard>
              <InfoCard label="Location">{startup.location || '\u2014'}</InfoCard>
              {startup.websiteUrl && (
                <InfoCard label="Website">
                  <a
                    href={startup.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {startup.websiteUrl}
                  </a>
                </InfoCard>
              )}
              <InfoCard label="Founded">
                {startup.foundingDate ? formatDate(startup.foundingDate) : '-'}
              </InfoCard>
              <InfoCard label="Validation Status">
                <Badge
                  variant={
                    startup.validationStatus === 'APPROVED'
                      ? 'success'
                      : startup.validationStatus === 'DECLINED'
                        ? 'destructive'
                        : 'secondary'
                  }
                  size="sm"
                >
                  {startup.validationStatus}
                </Badge>
              </InfoCard>
              <InfoCard label="Stealth Mode">
                <Badge variant={startup.isStealth ? 'default' : 'secondary'} size="sm">
                  {startup.isStealth ? 'Yes' : 'No'}
                </Badge>
              </InfoCard>
              {/* Which matcher treatment this founder's matches were built under.
                  "Not enrolled" is a real state, not a missing value: arms are
                  assigned when the outreach queue first flushes, so a startup
                  that has never started outreach has none. */}
              <InfoCard label="Matcher Experiment">
                <ExperimentArmBadges arms={startup.experimentArms} verbose />
              </InfoCard>
            </div>

            {/* Description */}
            {startup.description && (
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <p className="text-xs text-muted-foreground">Description</p>
                <div className="mt-1">
                  <Prose>{startup.description}</Prose>
                </div>
              </div>
            )}

            {/* Fundraising */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoCard label="Fundraising">
                <Badge variant={startup.isFundraising ? 'success' : 'secondary'} size="sm">
                  {startup.isFundraising ? 'Yes' : 'No'}
                </Badge>
              </InfoCard>
              <InfoCard label="Fundraising Stage">{startup.fundraisingStage || '\u2014'}</InfoCard>
              <InfoCard label="Fundraising Amount">
                {startup.fundraisingAmount || '\u2014'}
              </InfoCard>
              <InfoCard label="ARR">{startup.arr || '\u2014'}</InfoCard>
            </div>

            {/* Tags */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoCard label="Tags">
                {startup.tags && startup.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {startup.tags.map((tag: string) => (
                      <Badge key={tag} variant="secondary" size="sm">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  '-'
                )}
              </InfoCard>
            </div>

            {/* Images */}
            {startup.coverImageUrl && (
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <p className="text-xs text-muted-foreground">Cover Image</p>
                <div className="mt-2">
                  <img
                    src={startup.coverImageUrl}
                    alt="Cover"
                    className="max-h-48 rounded-md border border-border object-cover"
                  />
                </div>
              </div>
            )}

            {/* Team Members (employees) */}
            <TeamMembersSection startupId={id} />

            {/* Danger Zone */}
            <StartupDangerZone startupId={id} startupName={startup.name} />
          </div>
        )}

        {activeTab === 'outreach' && <OutreachSection startupId={id} />}

        {activeTab === 'users' && <UsersTab startupId={id} />}

        {activeTab === 'documents' && <DocumentsTab startupId={id} />}

        {activeTab === 'insights' && <InsightsTab startupId={id} />}

        {activeTab === 'matches' && <MatchesTab startupId={id} />}

        {activeTab === 'billing' && <BillingTab startupId={id} />}

        {activeTab === 'deal-flow' && <StartupDealFlowInteractionsTab startupId={id} />}

        {activeTab === 'entitlements' && <EntitlementsTab startupId={id} />}
        {activeTab === 'facts' && <StartupFactsTab startupId={id} />}

        {activeTab === 'email-updates' && <StartupEmailUpdatesTab startupId={id} />}
      </div>
    </>
  )
}

// ─── Team Tab ─────────────────────────────────────────────────────────────────

function UsersTab({ startupId }: { startupId: string }) {
  const membershipsQuery = useAdminStartupMembershipsQuery(startupId)
  const members = (membershipsQuery.data?.data ?? []) as AdminMembershipItem[]

  if (membershipsQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (members.length === 0) {
    return <div className="py-6 text-center text-sm text-muted-foreground">No members</div>
  }

  return (
    <div className="space-y-2">
      {members.map((m, i) => (
        <div
          key={m.userId ?? i}
          className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
        >
          <div>
            <p className="text-sm font-medium">{m.name || m.email || '\u2014'}</p>
            {m.email && m.name && <p className="text-xs text-muted-foreground">{m.email}</p>}
            {m.position && <p className="text-xs text-muted-foreground">{m.position}</p>}
          </div>
          <Badge variant="secondary" size="sm">
            {m.role}
          </Badge>
        </div>
      ))}
    </div>
  )
}

// ─── Documents Tab ────────────────────────────────────────────────────────────

type DocumentItem = {
  id: string
  documentId: string
  fileName: string
  type: string | null
  mimeType: string | null
  label: string | null
  createdAt: string | Date
}

function DocumentsTab({ startupId }: { startupId: string }) {
  const docsQuery = useAdminStartupDocumentsQuery(startupId)
  const docDownloadMutation = useAdminDocumentDownloadUrlMutation()
  const [previewDoc, setPreviewDoc] = useState<PreviewDocument | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const fetchDocUrl = async (docId: string) => {
    const res = await docDownloadMutation.mutateAsync({ path: { id: docId } })
    const data = res as { data?: { downloadUrl?: string } }
    return data.data?.downloadUrl ?? ''
  }

  if (docsQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const documents = (docsQuery.data?.data ?? []) as DocumentItem[]

  const columns: TableColumn<DocumentItem>[] = [
    {
      header: 'File Name',
      render: (doc) => (
        <button
          type="button"
          className="text-sm text-primary hover:underline"
          onClick={() => {
            setPreviewDoc({ id: doc.documentId, fileName: doc.fileName, mimeType: doc.mimeType })
            setPreviewOpen(true)
          }}
        >
          {doc.fileName}
        </button>
      ),
    },
    {
      header: 'Type',
      width: '140px',
      render: (doc) =>
        doc.type ? (
          <Badge variant="secondary" size="sm">
            {doc.type}
          </Badge>
        ) : (
          <CellText>{'\u2014'}</CellText>
        ),
    },
    {
      header: 'Label',
      width: '140px',
      render: (doc) => <CellText>{doc.label || '\u2014'}</CellText>,
    },
    {
      header: 'Created',
      width: '200px',
      render: (doc) => <CellText>{formatDate(doc.createdAt)}</CellText>,
    },
  ]

  return (
    <>
      <DataTable
        columns={columns}
        data={documents}
        isLoading={false}
        emptyMessage="No documents found."
        keyExtractor={(doc) => doc.id}
      />
      <DocumentPreviewModal
        document={previewDoc}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        fetchUrl={fetchDocUrl}
      />
    </>
  )
}

// ─── Matches Tab ──────────────────────────────────────────────────────────────

function scoreColor(score: number | null | undefined): string {
  if (score == null) return 'text-muted-foreground/50'
  if (score >= 75) return 'text-emerald-300'
  if (score >= 60) return 'text-amber-300'
  return 'text-red-300'
}

function ScoreCell({
  label,
  score,
  tooltip,
}: {
  label: string
  score: number | null | undefined
  tooltip: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className={cn('font-semibold', scoreColor(score))}>{score ?? '—'}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

const ACTIVITY_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> =
  {
    PENDING: 'secondary',
    ENQUEUED: 'warning',
    ACTIVE: 'warning',
    COMPLETED: 'success',
    FAILED: 'destructive',
    CANCELED: 'secondary',
  }

function ActivityResearchAction({
  investorId,
  contactCount,
}: {
  investorId: string
  contactCount: number
}) {
  const query = useAdminInvestorActivityResearchQuery(investorId)
  const triggerMutation = useAdminTriggerActivityResearchMutation(investorId)
  const latest = query.data?.data?.items?.[0]
  const status = latest?.status
  const isInFlight = status === 'PENDING' || status === 'ENQUEUED' || status === 'ACTIVE'
  const hasRun = !!latest
  const reason = latest?.reason ?? null

  return (
    <div className="flex shrink-0 items-center gap-2 text-[11px]">
      <Badge variant="secondary" size="sm">
        {contactCount} contact{contactCount === 1 ? '' : 's'}
      </Badge>
      {hasRun && status && (
        <button
          type="button"
          onClick={() => triggerMutation.mutate({ path: { id: investorId } })}
          disabled={triggerMutation.isPending || isInFlight}
          title="Click to rerun"
        >
          <Badge
            variant={ACTIVITY_STATUS_VARIANT[status] ?? 'secondary'}
            size="sm"
            className="cursor-pointer hover:opacity-70"
          >
            {(triggerMutation.isPending || isInFlight) && (
              <Loader2 className="mr-1 size-3 animate-spin" />
            )}
            {status}
          </Badge>
        </button>
      )}
      {reason && status === 'COMPLETED' && contactCount === 0 && (
        <>
          <span className="font-mono text-[10px] text-destructive" title="result_summary.reason">
            {reason}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => triggerMutation.mutate({ path: { id: investorId } })}
            disabled={triggerMutation.isPending || isInFlight}
          >
            {(triggerMutation.isPending || isInFlight) && (
              <Loader2 className="size-3 animate-spin" />
            )}
            Rerun
          </Button>
        </>
      )}
      {!hasRun || status === 'FAILED' || status === 'CANCELED' ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => triggerMutation.mutate({ path: { id: investorId } })}
          disabled={triggerMutation.isPending || isInFlight}
        >
          {(triggerMutation.isPending || isInFlight) && <Loader2 className="size-3 animate-spin" />}
          Run activity research
        </Button>
      ) : null}
    </div>
  )
}

const MATCHES_PAGE_SIZE = 25

function MatchesTab({ startupId }: { startupId: string }) {
  const [page, setPage] = useState(0)
  const matchesQuery = useAdminStartupMatchesQuery(startupId, page, MATCHES_PAGE_SIZE)
  const startupQuery = useAdminStartupByIdQuery(startupId)
  const refreshStatusQuery = useAdminStartupMatchesRefreshStatusQuery(startupId)
  const outreachQuery = useAdminStartupOutreachContactsQuery(startupId)
  const refreshMutation = useAdminRefreshStartupMatchesMutation(startupId)
  const matches = matchesQuery.data?.data?.matches ?? []
  const total = matchesQuery.data?.data?.total ?? 0
  const pageCount = Math.ceil(total / MATCHES_PAGE_SIZE)
  const pagedMatches = matches
  const outreachData = outreachQuery.data?.data as OutreachInsightsPayload | undefined
  const outreachByInvestor = new Map<string, OutreachInsightContact[]>()
  for (const item of outreachData?.items ?? []) {
    outreachByInvestor.set(item.investor_id, item.contacts)
  }
  const [exportProgress, setExportProgress] = useState<{
    done: number
    total: number
    failed: number
  } | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const isExporting = exportProgress !== null && exportProgress.done < exportProgress.total

  const handleExport = async () => {
    const startup = startupQuery.data?.data
    if (!startup) return
    const totalInvestors = total
    setExportError(null)
    setExportProgress({ done: 0, total: totalInvestors, failed: 0 })

    type EnginePage =
      | {
          startup?: { shared_body?: string | null }
          items?: Array<{ investor_id: string; contacts: unknown[] }>
          next_cursor?: string | null
        }
      | undefined

    const allItems: unknown[] = []
    let sharedBody: string | null | undefined
    let cursor: string | undefined
    let failed = 0
    let done = 0
    let pagesFetched = 0
    const MAX_RETRIES = 2
    while (true) {
      let attempt = 0
      let pageData: EnginePage
      // Retry the same cursor up to MAX_RETRIES extra times before aborting.
      while (true) {
        try {
          const res = await v2AdminGetStartupOutreachContacts({
            path: { id: startupId },
            query: { limit: 5, cursor, generateMissing: true },
            throwOnError: true,
          })
          pageData = (res.data?.data as EnginePage) ?? undefined
          break
        } catch (err) {
          if (attempt >= MAX_RETRIES) {
            const message = err instanceof Error ? err.message : 'Unknown error'
            failed += 1
            setExportProgress({ done, total: Math.max(totalInvestors, done), failed })
            const aborted = `Export aborted after ${pagesFetched} page(s): ${message}`
            setExportError(aborted)
            // Surface failure prominently — do NOT write a partial file.
            window.alert(aborted)
            return
          }
          attempt += 1
          // Small linear backoff: 250ms, 500ms.
          await new Promise((resolve) => setTimeout(resolve, 250 * attempt))
        }
      }

      if (sharedBody === undefined) sharedBody = pageData?.startup?.shared_body ?? null
      const pageItems = pageData?.items ?? []
      for (const item of pageItems) {
        allItems.push(item)
        done += 1
        setExportProgress({ done, total: Math.max(totalInvestors, done), failed })
      }
      pagesFetched += 1
      cursor = pageData?.next_cursor ?? undefined
      if (!cursor) break
    }

    const payload = {
      exportedAt: new Date().toISOString(),
      startup: {
        id: startup.id,
        name: startup.name,
        description: startup.description ?? null,
        sharedBody: sharedBody ?? null,
      },
      matches: allItems,
    }

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const slug = startup.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    a.href = url
    a.download = `matches-${slug || startup.id}-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
  const latestRefresh = refreshStatusQuery.data?.data
  const isInFlight =
    latestRefresh?.status === 'PENDING' ||
    latestRefresh?.status === 'ENQUEUED' ||
    latestRefresh?.status === 'PROCESSING'

  const handleRefresh = async () => {
    await refreshMutation.mutateAsync({ path: { id: startupId } })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-border pb-2">
        <div className="min-w-0 flex-1">
          {latestRefresh ? (
            <div
              className={cn(
                'flex items-center gap-2 text-xs',
                latestRefresh.status === 'FAILED'
                  ? 'text-red-300'
                  : isInFlight
                    ? 'text-amber-300'
                    : 'text-muted-foreground'
              )}
            >
              {isInFlight && <Loader2 className="size-3.5 animate-spin" />}
              <span className="truncate">
                Latest refresh:{' '}
                <span className="font-medium uppercase tracking-wide">{latestRefresh.status}</span>
                {' · '}
                {latestRefresh.triggerReason}
                {' · '}
                {formatDate(latestRefresh.createdAt)}
              </span>
              {latestRefresh.error && (
                <span className="ml-2 truncate font-mono text-[10px]">{latestRefresh.error}</span>
              )}
            </div>
          ) : (
            <h3 className="text-sm font-semibold">Recommended investors</h3>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {exportProgress && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {exportProgress.done}/{exportProgress.total}
              {exportProgress.failed > 0 ? ` · ${exportProgress.failed} failed` : ''}
            </span>
          )}
          {exportError && (
            <span className="max-w-[260px] truncate text-xs text-destructive" title={exportError}>
              {exportError}
            </span>
          )}
          <SecondaryButton onClick={handleExport} disabled={total === 0} loading={isExporting}>
            {isExporting ? 'Exporting…' : 'Export JSON'}
          </SecondaryButton>
          <CtaButton onClick={handleRefresh} loading={refreshMutation.isPending || isInFlight}>
            {refreshMutation.isPending || isInFlight ? 'Running…' : 'Regenerate'}
          </CtaButton>
        </div>
      </div>

      <StartupOutreachMessages
        msg1={outreachData?.startup?.outreach_msg1 ?? null}
        msg2={outreachData?.startup?.outreach_msg2 ?? null}
        msg3={outreachData?.startup?.outreach_msg3 ?? null}
        msg1Version={outreachData?.startup?.outreach_msg1_prompt_version ?? null}
        msg2Version={outreachData?.startup?.outreach_msg2_prompt_version ?? null}
        msg3Version={outreachData?.startup?.outreach_msg3_prompt_version ?? null}
        startupId={startupId}
      />

      {matchesQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : total === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No recommendations yet. Click Regenerate to enqueue a matcher run.
        </div>
      ) : (
        <div className="space-y-3">
          {pageCount > 1 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {page * MATCHES_PAGE_SIZE + 1}–{Math.min((page + 1) * MATCHES_PAGE_SIZE, total)} of{' '}
                {total}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          <div className="max-h-[calc(100vh-320px)] space-y-2 overflow-y-auto pr-1">
            {pagedMatches.map((m) => (
              <div
                key={m.id}
                className="rounded-lg border border-border bg-card px-4 py-2.5 text-sm"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <Link
                        to="/admin/investors/$id"
                        params={{ id: m.investorId }}
                        className="font-medium text-foreground hover:underline"
                      >
                        {m.investorName}
                      </Link>
                      {m.investorDescription && (
                        <span className="text-xs text-muted-foreground">
                          · {m.investorDescription}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 whitespace-pre-line text-xs text-foreground/85">
                      {m.reasoning.length > 300
                        ? `${m.reasoning.slice(0, 300).trimEnd()}…`
                        : m.reasoning}
                    </p>
                  </div>
                  <TooltipProvider delayDuration={150}>
                    <div className="flex shrink-0 items-center gap-3 text-right text-[11px] tabular-nums">
                      <ScoreCell
                        label="recombee"
                        score={m.recombeeScore}
                        tooltip="Recombee: vector similarity between startup and investor embeddings. Initial fit estimate (0–100)."
                      />
                      <ScoreCell
                        label="rerank"
                        score={m.rerankScore}
                        tooltip="Rerank: LLM-refined thesis fit score after reading the investor's thesis and the startup profile (0–100). This drives the founder-facing tier."
                      />
                      <ScoreCell
                        label="prescreen"
                        score={m.prescreeningScore}
                        tooltip="Prescreen: quick LLM judge pass before the full evaluation. Null until at least one prescreen has been run (0–100)."
                      />
                      <ScoreCell
                        label="deep eval"
                        score={m.evaluationScore}
                        tooltip="Deep eval: full investor-lens evaluation score from the most recent completed pre-pitch (0–100). Null until a founder runs one."
                      />
                    </div>
                  </TooltipProvider>
                </div>
                <div className="mt-2 flex justify-end">
                  <ActivityResearchAction
                    investorId={m.investorId}
                    contactCount={m.investorContacts.length}
                  />
                </div>
                <OutreachInsights
                  contacts={outreachByInvestor.get(m.investorId) ?? []}
                  sharedBody={outreachData?.startup?.shared_body ?? null}
                  isLoading={outreachQuery.isLoading}
                  startupId={startupId}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Billing Tab (pay-as-you-go) ─────────────────────────────────────────────

function BillingTab({ startupId }: { startupId: string }) {
  const billingQuery = useAdminStartupBillingQuery(startupId)
  const grantMutation = useAdminGrantStartupUsageMutation(startupId)
  const resetMutation = useAdminResetStartupGrantsMutation(startupId)

  const billing = billingQuery.data?.data
  const [grantQuantity, setGrantQuantity] = useState('1')

  const handleGrant = async (type: 'PITCH_SESSION' | 'EVALUATION') => {
    await grantMutation.mutateAsync({
      path: { id: startupId },
      body: { type, quantity: Math.max(1, Number.parseInt(grantQuantity, 10) || 1) },
    })
  }

  const handleReset = async () => {
    if (!window.confirm('Delete all unconsumed prepaid sessions & evaluations for this startup?')) {
      return
    }
    await resetMutation.mutateAsync({ path: { id: startupId } })
  }

  if (billingQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Balances */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <InfoCard label="Sessions available">
          <span className="tabular-nums">{billing?.sessionsAvailable ?? 0}</span>
        </InfoCard>
        <InfoCard label="Evaluations available">
          <span className="tabular-nums">{billing?.evaluationsAvailable ?? 0}</span>
        </InfoCard>
      </div>

      {/* Grant units */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Grant Units</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Grant free prepaid sessions or evaluations to this startup (no Stripe charge).
        </p>
        <div className="flex items-end gap-3">
          <div>
            <label htmlFor="grant-quantity" className="mb-1 block text-xs font-medium">
              Quantity
            </label>
            <input
              id="grant-quantity"
              type="number"
              min="1"
              max="100"
              value={grantQuantity}
              onChange={(e) => setGrantQuantity(e.target.value)}
              className="h-9 w-20 rounded-md border border-input bg-background px-3 text-sm tabular-nums"
            />
          </div>
          <SecondaryButton
            onClick={() => handleGrant('PITCH_SESSION')}
            loading={grantMutation.isPending}
          >
            {grantMutation.isPending ? 'Granting...' : '+ Sessions'}
          </SecondaryButton>
          <SecondaryButton
            onClick={() => handleGrant('EVALUATION')}
            loading={grantMutation.isPending}
          >
            {grantMutation.isPending ? 'Granting...' : '+ Evaluations'}
          </SecondaryButton>
        </div>

        <div className="mt-4 border-border border-t pt-4">
          <SecondaryButton tone="danger" onClick={handleReset} loading={resetMutation.isPending}>
            {resetMutation.isPending ? 'Resetting...' : 'Reset grants'}
          </SecondaryButton>
          <p className="mt-2 text-muted-foreground text-xs">
            Removes unconsumed prepaid units. Wallet credit and history are unaffected.
          </p>
        </div>
      </div>

      {/* Grant wallet credit */}
      <GrantWalletCreditCard startupId={startupId} />

      {/* Purchases log */}
      {billing?.purchases && billing.purchases.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold">Purchases ({billing.purchases.length})</h3>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {billing.purchases.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-xs"
              >
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" size="sm">
                    {p.type}
                  </Badge>
                  {p.consumedAt && (
                    <Badge variant="outline" size="sm">
                      consumed
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {p.amount > 0 ? (
                    <span className="tabular-nums font-medium">
                      {(p.amount / 100).toFixed(2)} {p.currency.toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">granted</span>
                  )}
                  <Badge variant={p.status === 'SUCCEEDED' ? 'success' : 'destructive'} size="sm">
                    {p.status}
                  </Badge>
                  <span className="text-muted-foreground">{formatDate(p.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Grant wallet credit (admin comps/refunds) ──────────────────────────────

const WALLET_TXN_LABELS: Record<string, string> = {
  WALLET_CREDIT: 'Wallet credit',
  WALLET_TOPUP: 'Top-up',
  PITCH_SESSION_CHARGE: 'Pitch session',
  EVALUATION_CHARGE: 'Fund evaluation',
  OUTREACH_TOPUP: 'Outreach top-up',
  OUTREACH_SETTLEMENT: 'Outreach settlement',
  OUTREACH_REFUND: 'Outreach refund',
}

function walletTxnLabel(type: string): string {
  return WALLET_TXN_LABELS[type] ?? type
}

function GrantWalletCreditCard({ startupId }: { startupId: string }) {
  const creditsQuery = useAdminStartupCreditsQuery(startupId)
  const addCreditsMutation = useAdminAddStartupCreditsMutation(startupId)

  const [amountStr, setAmountStr] = useState('')
  const [note, setNote] = useState('')

  const credits = creditsQuery.data?.status === 'ok' ? creditsQuery.data.data : undefined
  const dollars = Number(amountStr)
  const cents = Math.round(dollars * 100)
  const valid = !!amountStr && Number.isFinite(dollars) && cents > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) return
    await addCreditsMutation.mutateAsync({
      path: { id: startupId },
      body: { amount: cents, note: note.trim() || undefined },
    })
    setAmountStr('')
    setNote('')
  }

  // Only wallet/cent-denominated txns; legacy unit rows (ADMIN_TOPUP, PITCH_SESSION_USAGE)
  // would render via formatUsdCents as bogus dollar amounts (e.g. 3 units → "$0.03").
  const recent =
    credits?.transactions.filter((tx) => tx.type in WALLET_TXN_LABELS).slice(0, 6) ?? []

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold">Grant wallet credit</h3>
        <span className="font-mono text-sm font-semibold tabular-nums">
          {formatUsdCents(credits?.balance ?? 0)}
        </span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Add spendable wallet balance (comps/refunds). Counts toward the shared wallet — outreach,
        sessions, and evaluations.
      </p>

      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        <div>
          <label htmlFor="wallet-credit-amount" className="mb-1 block text-xs font-medium">
            $
          </label>
          <input
            id="wallet-credit-amount"
            type="number"
            min="0"
            step="0.01"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="25.00"
            className="h-9 w-28 rounded-md border border-input bg-background px-3 text-sm tabular-nums"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="wallet-credit-note" className="mb-1 block text-xs font-medium">
            Note
          </label>
          <input
            id="wallet-credit-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          />
        </div>
        <SecondaryButton type="submit" disabled={!valid} loading={addCreditsMutation.isPending}>
          {addCreditsMutation.isPending ? 'Granting...' : 'Grant'}
        </SecondaryButton>
      </form>

      {recent.length > 0 && (
        <div className="mt-4 space-y-1">
          {recent.map((tx) => (
            <div
              key={tx.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/40 px-3 py-1.5 text-xs"
            >
              <div className="min-w-0">
                <span className="font-medium">{walletTxnLabel(tx.type)}</span>
                {tx.note && <span className="ml-2 text-muted-foreground">{tx.note}</span>}
              </div>
              <div className="flex shrink-0 items-center gap-3 text-muted-foreground">
                {tx.adminUserName && <span>{tx.adminUserName}</span>}
                <span
                  className={cn(
                    'font-mono font-semibold tabular-nums',
                    tx.amount >= 0 ? 'text-emerald-500' : 'text-foreground'
                  )}
                >
                  {formatUsdCents(tx.amount)}
                </span>
                <span>{formatDate(tx.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Outreach status section (overview) ─────────────────────────────────────

const OUTREACH_STATUS_VARIANT: Record<
  OutreachStatus,
  'success' | 'warning' | 'secondary' | 'destructive'
> = {
  READY: 'success',
  AWAITING_SENDER: 'warning',
  AWAITING_CAMPAIGN: 'warning',
  PENDING_SEAT: 'secondary',
  PENDING_PROVISIONING: 'warning',
  PAUSED: 'secondary',
  PAUSED_DUE_TO_BUDGET: 'warning',
  ERROR: 'destructive',
  DISCONNECTING: 'warning',
  DISCONNECTED: 'secondary',
}

/**
 * Shows the deterministic HeyReach workspace name with a one-click copy (ops
 * paste it into HeyReach's workspace switcher) plus a shortcut to open the
 * workspace in HeyReach — the mock locally, real HeyReach in deployed envs.
 */
// The hand-written outreach client throws `${status} ${statusText}: ${body}`,
// where body is the JSON error envelope. Pull out the human-readable `message`
// so a 409 reason (e.g. "Workspace id 123 still exists in HeyReach") renders
// cleanly instead of as a raw JSON blob.
function extractApiErrorMessage(raw: string): string {
  const jsonStart = raw.indexOf('{')
  if (jsonStart === -1) return raw
  try {
    const parsed = JSON.parse(raw.slice(jsonStart)) as { message?: unknown }
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message
  } catch {
    // Not JSON — fall back to the raw string.
  }
  return raw
}

function CopyWorkspaceName({
  workspaceName,
  workspaceId,
}: {
  workspaceName: string
  workspaceId: number
}) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2">
      <code className="rounded bg-muted/50 px-1.5 py-0.5 text-[11px]">{workspaceName}</code>
      <SecondaryButton
        size="sm"
        title={`Copy "${workspaceName}" and open HeyReach`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(workspaceName)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          } catch {
            // Clipboard may be blocked; still open HeyReach.
          }
          window.open(heyreach.inboxUrl(workspaceId), '_blank', 'noopener,noreferrer')
        }}
      >
        {copied ? 'Copied ✓' : 'Copy & open ↗'}
      </SecondaryButton>
    </div>
  )
}

function OutreachSection({ startupId }: { startupId: string }) {
  const queryClient = useQueryClient()
  const [campaignTab, setCampaignTab] = useState<'investor' | 'portfolio'>('investor')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'outreach', 'status', startupId],
    queryFn: () => outreachApi.adminGetStatus(startupId),
    refetchInterval: 30_000,
  })
  const overviewQuery = useQuery({
    ...v2AdminGetStartupOutreachOverviewOptions({ path: { id: startupId } }),
    refetchInterval: 30_000,
  })
  const overview = overviewQuery.data?.status === 'ok' ? overviewQuery.data.data : null

  // Every live variant for both campaigns. Its own endpoint because the founder
  // one is gated on ownership and admins are not owners.
  const variantsQuery = useQuery({
    queryKey: ['admin', 'outreach', 'variants', startupId],
    queryFn: () => outreachApi.adminGetVariants(startupId),
  })
  const variants = variantsQuery.data ?? null

  const portfolioQueueQuery = useQuery(
    v2AdminGetStartupPortfolioOutreachQueueOptions({ path: { id: startupId } })
  )
  const portfolioQueue =
    portfolioQueueQuery.data?.status === 'ok' ? portfolioQueueQuery.data.data : null
  const portfolioStatusQuery = useQuery({
    ...v2AdminGetStartupPortfolioOutreachStatusOptions({ path: { id: startupId } }),
    refetchInterval: 30_000,
  })
  const portfolio =
    portfolioStatusQuery.data?.status === 'ok' ? portfolioStatusQuery.data.data : null

  // Raw HeyReach campaign/sender/invitation fields, for diagnosing stuck rows.
  const heyReachDebugQuery = useQuery({
    queryKey: ['admin', 'outreach', 'heyreach', startupId],
    queryFn: () => outreachApi.adminGetHeyReachDebug(startupId),
    refetchInterval: 30_000,
  })
  const heyReach = heyReachDebugQuery.data ?? null

  // Find the outreach row id (needed for Recheck / Clone) from the pending list.
  const pendingQuery = useQuery({
    queryKey: adminPendingQueryKey(),
    queryFn: outreachApi.adminPending,
  })
  const pendingItem = pendingQuery.data?.find((row) => row.startupId === startupId)
  const outreachRowId = pendingItem?.id ?? null

  const recheck = useMutation({
    mutationFn: (id: string) => outreachApi.adminRecheck(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPendingQueryKey() })
      queryClient.invalidateQueries({ queryKey: ['admin', 'outreach', 'status', startupId] })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupByIdQueryKey({ path: { id: startupId } }),
      })
    },
  })

  // Force-disconnect: admin has deleted the HeyReach workspace and wants us to
  // stop recreating it. The backend re-verifies the workspace is gone (409 if
  // not) before landing the row in DISCONNECTED.
  const [forceDisconnectError, setForceDisconnectError] = useState<string | null>(null)
  const forceDisconnect = useMutation({
    mutationFn: () => outreachApi.adminForceDisconnect(startupId),
    onSuccess: () => {
      setForceDisconnectError(null)
      queryClient.invalidateQueries({ queryKey: ['admin', 'outreach', 'status', startupId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'outreach', 'heyreach', startupId] })
      queryClient.invalidateQueries({ queryKey: adminPendingQueryKey() })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupOutreachOverviewOptions({ path: { id: startupId } }).queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupPortfolioOutreachStatusOptions({ path: { id: startupId } })
          .queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: v2AdminGetStartupByIdQueryKey({ path: { id: startupId } }),
      })
    },
    onError: (error) => setForceDisconnectError(extractApiErrorMessage(error.message)),
  })

  const limitsPendingQuery = useQuery({
    queryKey: adminLimitsPendingQueryKey(),
    queryFn: outreachApi.adminLimitsPending,
  })
  const limitsRowId =
    limitsPendingQuery.data?.find((row) => row.startupId === startupId)?.id ?? null

  const markLimitsSet = useMutation({
    mutationFn: (id: string) => outreachApi.adminMarkLimitsSet(id),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: adminLimitsPendingQueryKey() }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'outreach', 'status', startupId] }),
      ]),
  })

  const outreachContactsQuery = useAdminStartupOutreachContactsQuery(startupId)
  const billingQuery = useQuery(v2AdminListOutreachBillingOptions())
  const billingItem: BillingRow | undefined =
    billingQuery.data?.status === 'ok'
      ? (billingQuery.data.data.items as BillingRow[]).find((r) => r.startupId === startupId)
      : undefined

  if (isLoading) return null
  if (error) {
    return (
      <div>
        <h3 className="mb-3 text-sm font-semibold">Outreach</h3>
        <p className="text-xs text-destructive">Failed to load outreach status.</p>
      </div>
    )
  }
  if (!data) return null

  const { lastError, heyReachWorkspaceId: workspaceId, heyReachWorkspaceName: workspaceName } = data
  const startupMsgs = outreachContactsQuery.data?.data as OutreachInsightsPayload | undefined

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold">Outreach</h3>

      {/* Shared: HeyReach workspace + sender (common to both campaigns) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {workspaceName && workspaceId != null && (
          <InfoCard label="HeyReach workspace">
            <CopyWorkspaceName workspaceName={workspaceName} workspaceId={workspaceId} />
          </InfoCard>
        )}
        <InfoCard label="HeyReach sender ID">
          <span className="font-mono tabular-nums">{heyReach?.heyReachSenderId ?? '—'}</span>
        </InfoCard>
        <InfoCard label="Invitation ID">
          <span className="font-mono tabular-nums">{heyReach?.heyReachInvitationId ?? '—'}</span>
        </InfoCard>
        <InfoCard label="Invited email">
          <span className="break-all font-mono text-[11px]">{heyReach?.invitedEmail ?? '—'}</span>
        </InfoCard>
        {heyReach?.invitationUrl && (
          <InfoCard label="Invitation URL">
            <a
              href={heyReach.invitationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all font-mono text-[11px] text-primary hover:underline"
            >
              {heyReach.invitationUrl}
            </a>
          </InfoCard>
        )}
        {/* Status + trial are properties of the single StartupOutreach row, so
            they're shared across both campaigns and live in this top section. */}
        <InfoCard label="Status">
          <div className="flex flex-wrap items-center gap-2">
            {data.status ? (
              <Badge variant={OUTREACH_STATUS_VARIANT[data.status] ?? 'secondary'} size="sm">
                {data.status}
              </Badge>
            ) : (
              '—'
            )}
            {outreachRowId && (
              <SecondaryButton
                size="sm"
                onClick={() => recheck.mutate(outreachRowId)}
                loading={recheck.isPending}
              >
                {recheck.isPending ? 'Rechecking…' : 'Recheck'}
              </SecondaryButton>
            )}
            {limitsRowId && (
              <CtaButton
                size="sm"
                onClick={() => markLimitsSet.mutate(limitsRowId)}
                loading={markLimitsSet.isPending}
              >
                {markLimitsSet.isPending ? 'Saving…' : 'Limits set'}
              </CtaButton>
            )}
            {data.status && data.status !== 'DISCONNECTED' && (
              <ConfirmButton
                confirmLabel="Confirm?"
                className="h-8 w-auto whitespace-nowrap px-3 text-xs normal-case"
                disabled={forceDisconnect.isPending}
                onConfirm={() => forceDisconnect.mutate()}
              >
                {forceDisconnect.isPending ? 'Disconnecting…' : 'Force disconnect'}
              </ConfirmButton>
            )}
          </div>
          {forceDisconnectError && (
            <p className="mt-1.5 text-[10px] text-destructive">{forceDisconnectError}</p>
          )}
        </InfoCard>
        <InfoCard label="Trial ends">
          <span className="tabular-nums text-[11px]">
            {data.trialEndsAt ? new Date(data.trialEndsAt).toLocaleString() : '—'}
          </span>
        </InfoCard>
      </div>

      {/* Per-campaign tabs */}
      <div className="mt-5 border-b border-border/40">
        <div className="flex gap-0">
          {(['investor', 'portfolio'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setCampaignTab(t)}
              className={`px-4 pb-2 pt-1 text-[12.5px] font-semibold transition-colors relative ${campaignTab === t ? 'text-foreground' : 'text-muted-foreground hover:text-foreground/80'}`}
            >
              {t === 'investor' ? 'Direct Investors' : 'Portfolio Founders'}
              {campaignTab === t && (
                <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] rounded-full bg-primary" />
              )}
            </button>
          ))}
        </div>
      </div>

      {campaignTab === 'investor' && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <InfoCard label="Enabled">
              <Badge
                variant={data.directInvestorOutreachEnabled !== false ? 'default' : 'secondary'}
                size="sm"
              >
                {data.directInvestorOutreachEnabled !== false ? 'Enabled' : 'Disabled'}
              </Badge>
            </InfoCard>
            <InfoCard label="Campaign ID">
              <span className="font-mono tabular-nums">{heyReach?.heyReachCampaignId ?? '—'}</span>
            </InfoCard>
            <InfoCard label="Limits set at">
              <span className="tabular-nums">
                {data.limitsUpdatedAt ? new Date(data.limitsUpdatedAt).toLocaleString() : '—'}
              </span>
            </InfoCard>
            <InfoCard label="Last lead sync">
              <span className="tabular-nums">
                {overview?.lastLeadFlushedAt
                  ? new Date(overview.lastLeadFlushedAt).toLocaleString()
                  : '—'}
              </span>
            </InfoCard>
            {lastError && (
              <InfoCard label="Last error">
                <span className="font-mono text-[11px] text-destructive">{lastError}</span>
              </InfoCard>
            )}
          </div>
          <StartupOutreachMessages
            msg1={startupMsgs?.startup?.outreach_msg1 ?? null}
            msg2={startupMsgs?.startup?.outreach_msg2 ?? null}
            msg3={startupMsgs?.startup?.outreach_msg3 ?? null}
            msg1Version={startupMsgs?.startup?.outreach_msg1_prompt_version ?? null}
            msg2Version={startupMsgs?.startup?.outreach_msg2_prompt_version ?? null}
            msg3Version={startupMsgs?.startup?.outreach_msg3_prompt_version ?? null}
            startupId={startupId}
            variants={variants?.investor}
          />
          {overview && <OutreachFunnel funnel={overview.funnel} />}
        </div>
      )}

      {campaignTab === 'portfolio' && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <InfoCard label="Enabled">
              <Badge variant={portfolio?.enabled ? 'default' : 'secondary'} size="sm">
                {portfolio?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </InfoCard>
            <InfoCard label="Campaign ID">
              <span className="font-mono tabular-nums">{portfolio?.campaignId ?? '—'}</span>
            </InfoCard>
            <InfoCard label="Limits set at">
              {/* Limits are a per-startup gate shared across both channels. */}
              <span className="tabular-nums text-[11px]">
                {data.limitsUpdatedAt ? new Date(data.limitsUpdatedAt).toLocaleString() : '—'}
              </span>
            </InfoCard>
            <InfoCard label="Contacts found">
              <span className="tabular-nums">{portfolio?.contactsCount ?? '—'}</span>
            </InfoCard>
            <InfoCard label="Messages reviewed">
              <Badge variant={portfolio?.messagesReviewedAt ? 'default' : 'secondary'} size="sm">
                {portfolio?.messagesReviewedAt ? 'Yes' : 'No'}
              </Badge>
            </InfoCard>
            <InfoCard label="Last lead sync">
              <span className="tabular-nums">
                {portfolio?.lastLeadSyncedAt
                  ? new Date(portfolio.lastLeadSyncedAt).toLocaleString()
                  : '—'}
              </span>
            </InfoCard>
            {portfolio?.lastError && (
              <InfoCard label="Last error">
                <span className="font-mono text-[11px] text-destructive">
                  {portfolio.lastError}
                </span>
              </InfoCard>
            )}
          </div>
          <PortfolioOutreachMessages
            msg1={portfolio?.msg1}
            msg2={portfolio?.msg2}
            msg3={portfolio?.msg3}
            msg1Version={portfolio?.msg1PromptVersion}
            msg2Version={portfolio?.msg2PromptVersion}
            msg3Version={portfolio?.msg3PromptVersion}
            startupId={startupId}
            variants={variants?.portfolio}
          />
          <PortfolioOutreachFunnel queue={portfolioQueue} />
        </div>
      )}

      {/* Section C — Outreach billing for this startup */}
      <div className="mt-6">
        <h4 className="mb-2 text-sm font-semibold">Outreach billing</h4>
        {billingQuery.isLoading ? (
          <div className="rounded-xl border border-border/60 bg-card/30 px-5 py-6 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : !billingItem ? (
          <div className="rounded-xl border border-border/60 bg-card/30 px-5 py-6 text-center text-sm text-muted-foreground">
            No billing record yet for this startup.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border/60">
            <table className="w-full text-sm">
              <OutreachBillingTableHeader />
              <tbody>
                <OutreachBillingRow row={billingItem} />
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Team Members Section (employees, on overview) ───────────────────────────

function TeamMembersSection({ startupId }: { startupId: string }) {
  const { data: members, isLoading } = useAdminStartupTeamMembersQuery(startupId)

  if (isLoading) return null

  const items = members?.data ?? []
  if (items.length === 0) return null

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold">Team ({items.length})</h3>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((m) => (
          <div key={m.id} className="rounded-lg border border-border bg-card px-4 py-3">
            <p className="text-sm font-medium">{m.name}</p>
            {m.position && <p className="text-xs text-muted-foreground">{m.position}</p>}
            {m.linkedinUrl && (
              <a
                href={m.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline"
              >
                LinkedIn
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Insights Tab ─────────────────────────────────────────────────────

function InsightsTab({ startupId }: { startupId: string }) {
  const textFeedbacksQuery = useAdminStartupTextFeedbacksQuery(startupId)
  const reactionsQuery = useAdminStartupReactionsQuery(startupId)
  const reportsQuery = useAdminAiInsightReportsQuery()
  const regenerateMutation = useAdminRegenerateAiInsightReportMutation()

  const textFeedbacks = textFeedbacksQuery.data?.data ?? []
  const reactions = reactionsQuery.data?.data ?? []
  const reports = reportsQuery.data?.data
  const reportItems = Array.isArray(reports) ? reports : (reports as { items?: unknown[] })?.items
  const report = Array.isArray(reportItems)
    ? reportItems.find((r: { startupId?: string }) => r.startupId === startupId)
    : undefined

  return (
    <div className="space-y-8">
      {/* AI Report */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold">AI Engagement Report</h3>
          <SecondaryButton
            size="sm"
            onClick={() => regenerateMutation.mutate({ body: { startupId } })}
            loading={regenerateMutation.isPending}
          >
            {regenerateMutation.isPending ? 'Generating...' : 'Regenerate report'}
          </SecondaryButton>
        </div>
        {report ? (
          <div className="rounded-xl border border-border bg-card/30 p-5">
            <p className="mb-3 text-xs text-muted-foreground">
              Generated {formatDate(report.generatedAt)}
              {report.hasNewData && (
                <span className="ml-2 rounded bg-amber-400/10 px-1.5 py-0.5 text-[10px] text-amber-400">
                  New data available
                </span>
              )}
            </p>
            <AiReportSection
              report={report.report as AiReportData}
              generatedAt={report.generatedAt.toString()}
              loading={false}
            />
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No AI report generated yet.
          </p>
        )}
      </section>

      {/* Text Feedbacks */}
      <section>
        <h3 className="mb-4 text-sm font-semibold">
          Text Highlight Feedbacks ({textFeedbacks.length})
        </h3>
        {textFeedbacks.length > 0 ? (
          <div className="space-y-3">
            {textFeedbacks.map((f) => (
              <div key={f.id} className="rounded-xl border border-border p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className={cn(
                      'rounded px-2 py-0.5 text-xs font-medium',
                      f.reaction === 'up'
                        ? 'bg-emerald-400/10 text-emerald-400'
                        : 'bg-red-400/10 text-red-400'
                    )}
                  >
                    {f.reaction === 'up' ? 'Agree' : 'Disagree'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {visitorLabel(f.visitorEmail)}
                  </span>
                  <span className="text-xs text-muted-foreground/50">·</span>
                  <span className="text-xs text-muted-foreground">{f.linkName}</span>
                  <span className="text-xs text-muted-foreground/50">·</span>
                  <span className="text-xs text-muted-foreground">{formatDate(f.createdAt)}</span>
                </div>
                <p className="text-sm leading-relaxed text-foreground">"{f.selectedText}"</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">No text feedback yet.</p>
        )}
      </section>

      {/* Statement Reactions */}
      <section>
        <h3 className="mb-4 text-sm font-semibold">Statement Reactions ({reactions.length})</h3>
        {reactions.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="px-4 py-2 text-left font-medium">Statement</th>
                  <th className="px-4 py-2 text-left font-medium">Type</th>
                  <th className="px-4 py-2 text-left font-medium">Reaction</th>
                  <th className="px-4 py-2 text-left font-medium">Visitor</th>
                  <th className="px-4 py-2 text-left font-medium">Link</th>
                  <th className="px-4 py-2 text-left font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {reactions.map((r) => (
                  <tr key={r.id}>
                    <td className="max-w-xs truncate px-4 py-2 text-foreground">{r.statementId}</td>
                    <td className="px-4 py-2 text-muted-foreground">{r.statementType}</td>
                    <td className="px-4 py-2">
                      <span
                        className={cn(
                          'rounded px-2 py-0.5 text-xs font-medium',
                          r.reaction === 'up'
                            ? 'bg-emerald-400/10 text-emerald-400'
                            : 'bg-red-400/10 text-red-400'
                        )}
                      >
                        {r.reaction === 'up' ? 'Agree' : 'Disagree'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {visitorLabel(r.visitorEmail)}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{r.linkName}</td>
                    <td className="px-4 py-2 text-muted-foreground">{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No statement reactions yet.
          </p>
        )}
      </section>
    </div>
  )
}

// ─── Entitlements Tab ───────────────────────────────────────────────────────

const ENTITLEMENT_KIND_OPTIONS = [
  { value: ENTITLEMENT_KIND.INVESTOR_MATCHES_UNLOCK, label: 'Investor Matches Unlock' },
] as const

/** The startup's daily profile-chat answer ceiling. Empty restores the default. */
function ProfileChatLimitCard({ startupId }: { startupId: string }) {
  const startupQuery = useAdminStartupByIdQuery(startupId)
  const setLimitMutation = useAdminSetStartupProfileChatLimitMutation(startupId)
  const current = startupQuery.data?.data?.profileChatDailyLimit ?? null
  const [draft, setDraft] = useState('')

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = draft.trim()
    await setLimitMutation.mutateAsync({
      path: { id: startupId },
      body: { profileChatDailyLimit: trimmed === '' ? null : Number(trimmed) },
    })
    setDraft('')
  }

  return (
    <form onSubmit={handleSave} className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-semibold">Profile chat daily limit</h3>
      <p className="text-xs text-muted-foreground">
        Answers per day across all of this startup's links. Currently {current ?? 'the default'}.
        Leave empty to restore the default.
      </p>
      <div className="flex items-end gap-3">
        <input
          type="number"
          min={1}
          step={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={current != null ? String(current) : 'Default'}
          aria-label="Answers per day"
          className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <Button type="submit" size="sm" disabled={setLimitMutation.isPending}>
          Save
        </Button>
      </div>
    </form>
  )
}

/** SeedForge's own test or demo startup: kept out of investor discovery whatever its stealth switch says. */
function InternalStartupCard({ startupId }: { startupId: string }) {
  const startupQuery = useAdminStartupByIdQuery(startupId)
  const setInternalMutation = useAdminSetStartupInternalMutation(startupId)
  const isInternal = startupQuery.data?.data?.isInternal ?? false

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Internal startup</h3>
        <p className="text-xs text-muted-foreground">
          SeedForge's own test or demo startup. Never shown to investors in search or deal flow,
          even when it is out of stealth. Its profile links keep working.
        </p>
      </div>
      <Switch
        checked={isInternal}
        disabled={startupQuery.isLoading || setInternalMutation.isPending}
        onCheckedChange={(checked) =>
          setInternalMutation.mutate({ path: { id: startupId }, body: { isInternal: checked } })
        }
        aria-label="Internal startup"
      />
    </div>
  )
}

function EntitlementsTab({ startupId }: { startupId: string }) {
  const entitlementsQuery = useAdminStartupEntitlementsQuery(startupId)
  const grantMutation = useAdminGrantStartupEntitlementMutation(startupId)
  const revokeMutation = useAdminRevokeStartupEntitlementMutation(startupId)

  const [kind, setKind] = useState<(typeof ENTITLEMENT_KIND_OPTIONS)[number]['value']>(
    ENTITLEMENT_KIND.INVESTOR_MATCHES_UNLOCK
  )
  const [reason, setReason] = useState('')
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null)
  const [revokeReason, setRevokeReason] = useState('')

  const items = entitlementsQuery.data?.data?.items ?? []
  const hasActive = items.some((e) => e.kind === kind && e.status === ENTITLEMENT_STATUS.ACTIVE)

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reason.trim()) return
    await grantMutation.mutateAsync({
      path: { startupId },
      body: { kind, reason: reason.trim() },
    })
    setReason('')
  }

  const openRevokeDialog = (entitlementId: string) => {
    setRevokeTarget(entitlementId)
    setRevokeReason('')
  }

  const closeRevokeDialog = () => {
    setRevokeTarget(null)
    setRevokeReason('')
  }

  const handleRevokeConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!revokeTarget || !revokeReason.trim()) return
    await revokeMutation.mutateAsync({
      path: { startupId, entitlementId: revokeTarget },
      body: { reason: revokeReason.trim() },
    })
    closeRevokeDialog()
  }

  if (entitlementsQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <InternalStartupCard startupId={startupId} />
      <ProfileChatLimitCard startupId={startupId} />

      {/* Grant form */}
      <form
        onSubmit={handleGrant}
        className="space-y-3 rounded-lg border border-border bg-card p-4"
      >
        <h3 className="text-sm font-semibold">Grant entitlement</h3>
        <p className="text-xs text-muted-foreground">
          Bypasses Stripe — records an audit trail with your user id and reason. Same engine fan-out
          runs as a paid unlock.
        </p>
        <div className="grid items-end gap-3 sm:grid-cols-[1fr_2fr_auto]">
          <div>
            <label htmlFor="entitlement-kind" className="mb-1 block text-xs font-medium">
              Kind
            </label>
            <select
              id="entitlement-kind"
              value={kind}
              onChange={(e) =>
                setKind(e.target.value as (typeof ENTITLEMENT_KIND_OPTIONS)[number]['value'])
              }
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              {ENTITLEMENT_KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="entitlement-reason" className="mb-1 block text-xs font-medium">
              Reason
            </label>
            <input
              id="entitlement-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required) — e.g. comp for support issue"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <CtaButton
            type="submit"
            size="sm"
            disabled={!reason.trim() || hasActive}
            loading={grantMutation.isPending}
          >
            {hasActive ? 'Already active' : 'Grant'}
          </CtaButton>
        </div>
        {grantMutation.isError && (
          <p className="text-xs text-destructive">
            {(grantMutation.error as Error)?.message ?? 'Grant failed'}
          </p>
        )}
      </form>

      {/* List */}
      <div>
        <h3 className="mb-3 text-sm font-semibold">History</h3>
        {items.length === 0 ? (
          <p className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            No entitlements yet.
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Kind</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-t border-border">
                    <td className="px-4 py-2">{e.kind}</td>
                    <td className="px-4 py-2">
                      <Badge
                        variant={
                          e.status === ENTITLEMENT_STATUS.ACTIVE
                            ? 'success'
                            : e.status === ENTITLEMENT_STATUS.REVOKED ||
                                e.status === ENTITLEMENT_STATUS.REFUNDED
                              ? 'destructive'
                              : 'secondary'
                        }
                        size="sm"
                      >
                        {e.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{e.source}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {e.grantedReason ?? e.revokedReason ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{formatDate(e.createdAt)}</td>
                    <td className="px-4 py-2 text-right">
                      {e.status === ENTITLEMENT_STATUS.ACTIVE && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => openRevokeDialog(e.id)}
                          disabled={revokeMutation.isPending}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeRevokeDialog()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke entitlement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRevokeConfirm} className="space-y-4">
            <div>
              <label htmlFor="revoke-reason" className="mb-1 block text-xs font-medium">
                Reason
              </label>
              <input
                id="revoke-reason"
                type="text"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="Why is this entitlement being revoked?"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={closeRevokeDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={!revokeReason.trim() || revokeMutation.isPending}
              >
                {revokeMutation.isPending ? 'Revoking…' : 'Revoke'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function StartupDangerZone({ startupId, startupName }: { startupId: string; startupName: string }) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const navigate = useNavigate()

  return (
    <div className="border-t border-border pt-6">
      <div className="rounded-md border border-red-500/20 bg-red-500/5 p-4">
        <h3 className="text-sm font-medium text-red-500">Danger Zone</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Permanently delete this startup and all associated data.
        </p>
        <SecondaryButton
          tone="danger"
          size="sm"
          className="mt-3"
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete startup
        </SecondaryButton>
      </div>

      <DeleteStartupDialog
        startupId={startupId}
        startupName={startupName}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDeleted={() => navigate({ to: '/admin/startups' })}
      />
    </div>
  )
}

const STARTUP_DELETION_COUNT_LABELS: { key: string; label: string }[] = [
  { key: 'teamMembers', label: 'Team members' },
  { key: 'evaluations', label: 'Evaluations' },
  { key: 'creditTransactions', label: 'Credit transactions' },
  { key: 'usagePurchases', label: 'Usage purchases' },
  { key: 'entitlements', label: 'Entitlements' },
  { key: 'aiInsightReports', label: 'AI reports' },
  { key: 'dealFlowMatches', label: 'Deal-flow matches' },
  { key: 'dealFlowInteractions', label: 'Deal-flow interactions' },
  { key: 'contactOutreachMessages', label: 'Outreach messages' },
  { key: 'outreachLeads', label: 'Outreach leads' },
  { key: 'outreachEvents', label: 'Outreach events' },
  { key: 'outreachAccruals', label: 'Outreach accruals' },
  { key: 'invites', label: 'Invites' },
  { key: 'investorMatches', label: 'Investor matches' },
  { key: 'matchRefreshRequests', label: 'Match refresh requests' },
]

function DeleteStartupDialog({
  startupId,
  startupName,
  open,
  onOpenChange,
  onDeleted,
}: {
  startupId: string
  startupName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted: () => void
}) {
  const [confirmName, setConfirmName] = useState('')
  const [forfeitBalance, setForfeitBalance] = useState(false)

  // See the user dialog: a stale acknowledgement must not survive a reopen.
  useEffect(() => {
    if (!open) {
      setForfeitBalance(false)
      setConfirmName('')
    }
  }, [open])

  const previewQuery = useAdminStartupDeletionPreviewQuery(startupId, open)
  const deleteMutation = useAdminDeleteStartupMutation()

  const preview = previewQuery.data?.data

  // Deleting the startup destroys this money, so the backend refuses without
  // an explicit forfeit. Without the checkbox a funded startup would be
  // undeletable from this page.
  const balanceCents = preview?.walletBalanceCents ?? 0

  const blockers = preview?.heyReachBlockers ?? []

  const canConfirm =
    confirmName === startupName &&
    !deleteMutation.isPending &&
    blockers.length === 0 &&
    (balanceCents <= 0 || forfeitBalance)

  const handleDelete = () => {
    deleteMutation.mutate(
      { path: { id: startupId }, body: { forfeitBalance } },
      { onSuccess: () => onDeleted() }
    )
  }

  const summaryGroups = preview
    ? [
        { label: 'Documents', items: preview.documents.map((d) => d.fileName) },
        { label: 'Members', items: preview.members.map((m) => m.name) },
      ]
    : []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete Startup</DialogTitle>
          <DialogDescription>
            This action is irreversible. All startup data will be permanently deleted.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {previewQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Loading deletion preview…</p>
          )}
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Data rooms:</span>{' '}
                  <strong>{preview.dataRooms.length}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Pitch sessions:</span>{' '}
                  <strong>{preview.pitchSessions.length}</strong>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <span className="text-muted-foreground">Prescreens:</span>{' '}
                  <strong>{preview.prescreens.length}</strong>
                </div>
                {STARTUP_DELETION_COUNT_LABELS.map(({ key, label }) => (
                  <div key={key} className="rounded border border-border px-3 py-2">
                    <span className="text-muted-foreground">{label}:</span>{' '}
                    <strong>{preview.counts[key as keyof typeof preview.counts]}</strong>
                  </div>
                ))}
              </div>

              {summaryGroups.map(
                (group) =>
                  group.items.length > 0 && (
                    <div key={group.label}>
                      <p className="mb-1.5 text-xs font-medium text-red-400">
                        {group.label} ({group.items.length}):
                      </p>
                      <div className="space-y-1">
                        {group.items.map((name, index) => (
                          <div
                            key={`${group.label}-${index}`}
                            className="truncate rounded border border-border px-3 py-1.5"
                          >
                            {name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
              )}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="confirm-startup-name" className="text-sm">
              Type <span className="font-mono font-medium">{startupName}</span> to confirm
            </label>
            <Input
              id="confirm-startup-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={startupName}
            />
          </div>
          <HeyReachBlockers blockers={blockers} />
          {balanceCents > 0 && (
            <ForfeitBalanceConfirm
              amountCents={balanceCents}
              checked={forfeitBalance}
              onCheckedChange={setForfeitBalance}
            />
          )}
          {deleteMutation.isError && (
            <p className="text-sm text-red-500">
              {apiErrorMessage(deleteMutation.error, 'Failed to delete startup. Please try again.')}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!canConfirm}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete Startup'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
